import { useSortable, SortableTh } from "@/components/common/SortableTable";
import { useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { TID } from "@/constants/testIds";
import { KpiCard } from "@/components/common/KpiCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  FileSignature, PlusCircle, Search, ShieldAlert, Trash2, RefreshCcw,
  AlertCircle, Calendar, DollarSign, User, Loader2, TimerReset,
  Mail, Upload, Sparkles, Copy, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const CONTRACT_TYPES = [
  { value: "service_agreement", label: "Service Agreement" },
  { value: "maintenance",       label: "Maintenance" },
  { value: "warranty",          label: "Warranty" },
  { value: "supply",            label: "Supply" },
  { value: "framework",         label: "Framework" },
  { value: "other",             label: "Other" },
];

const CONTRACT_STATUSES = [
  { value: "active",     label: "Active" },
  { value: "expired",    label: "Expired" },
  { value: "renewed",    label: "Renewed" },
  { value: "terminated", label: "Terminated" },
  { value: "draft",      label: "Draft" },
];

const BUCKET_COLOURS = {
  expired:      "bg-rose-100 text-rose-800 border-rose-200",
  expiring_30:  "bg-amber-100 text-amber-800 border-amber-200",
  expiring_90:  "bg-yellow-50 text-yellow-800 border-yellow-200",
  active:       "bg-emerald-50 text-emerald-800 border-emerald-200",
  unknown:      "bg-slate-100 text-slate-600 border-slate-200",
};
const BUCKET_LABEL = {
  expired: "Expired", expiring_30: "≤ 30 days", expiring_90: "≤ 90 days",
  active: "Active", unknown: "No end date",
};

const RENEWAL_ACTIONS = [
  { value: "renewed",           label: "Renewed" },
  { value: "extended",          label: "Extended" },
  { value: "under_negotiation", label: "Under negotiation" },
  { value: "not_renewed",       label: "Not renewed" },
  { value: "terminated",        label: "Terminated" },
];

const fmtMoney = (n) =>
  n == null ? "—" : "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

const fmtDate = (s) => (s ? new Date(s.slice(0, 10)).toLocaleDateString() : "—");

const monthLabel = (yyyymm) => {
  if (!yyyymm) return "";
  const [y, m] = yyyymm.split("-").map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleDateString(undefined, { month: "short", year: "2-digit" });
};

// ============================================================
// Contracts page
// ============================================================
export default function ContractsPage() {
  const { can, loaded } = usePermissions();
  const canView   = can("contracts", "view");
  const canCreate = can("contracts", "create");
  const canEdit   = can("contracts", "edit");
  const canDelete = can("contracts", "delete");

  const [stats, setStats] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [cpiSchedule, setCpiSchedule] = useState([]);
  const [rows, setRows] = useState([]);
  const { sorted: sortedContracts, sort: contractSort, setSort: setContractSort } = useSortable(rows);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [expiringOnly, setExpiringOnly] = useState(false);

  // Dialogs
  const [createOpen, setCreateOpen] = useState(false);
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewTarget, setRenewTarget] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [pdfImportOpen, setPdfImportOpen] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      if (status) params.set("status", status);
      if (type) params.set("contract_type", type);
      if (expiringOnly) params.set("expiring_within_days", "90");
      const [{ data: s }, { data: tl }, { data: list }, { data: cpi }] = await Promise.all([
        api.get("/contracts/stats"),
        api.get("/contracts/renewal-timeline"),
        api.get(`/contracts?${params.toString()}`),
        api.get("/contracts/cpi-schedule?days=90"),
      ]);
      setStats(s);
      setTimeline(tl);
      setRows(list);
      setCpiSchedule(cpi?.items || []);
    } catch (e) {
      toast.error("Contracts failed to load", { description: formatApiErrorDetail(e) });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    load();
    // eslint-disable-next-line
  }, [canView, search, status, type, expiringOnly]);

  const maxMonthValue = useMemo(() => Math.max(1, ...timeline.map((m) => m.value || 0)), [timeline]);

  if (!loaded) return <AppLayout><div className="p-8">Loading…</div></AppLayout>;
  if (!canView) {
    return (
      <AppLayout>
        <div className="max-w-md mx-auto mt-24 rounded-lg border border-slate-200 bg-white p-6 text-center">
          <ShieldAlert className="w-8 h-8 mx-auto text-rose-500 mb-2" />
          <div className="font-semibold text-slate-800 mb-1">Restricted</div>
          <p className="text-sm text-slate-600">Your role does not have access to the Contracts register.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div data-testid="contracts-page" className="p-6 max-w-[1400px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display text-slate-900 flex items-center gap-2">
              <FileSignature className="w-7 h-7 text-gbe" />
              Contracts
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Central register of client contracts, service agreements & warranties. Section 12 of the scope.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={load} disabled={refreshing}
              data-testid="contracts-refresh-button"
            >
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
              <span className="ml-2">Refresh</span>
            </Button>
            {canCreate && (
              <Button
                variant="outline" size="sm"
                onClick={() => setPdfImportOpen(true)}
                data-testid={TID.contractsPdfImportOpen}
              >
                <Sparkles className="w-4 h-4 mr-2" />Import from PDF
              </Button>
            )}
            {canCreate && (
              <Button
                onClick={() => setCreateOpen(true)}
                data-testid={TID.contractsCreateOpen}
                className="bg-gbe hover:bg-gbe/90 text-white"
                size="sm"
              >
                <PlusCircle className="w-4 h-4 mr-2" />New contract
              </Button>
            )}
          </div>
        </div>

        {/* KPI row — wired to metric_dictionary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard metricId="CON-KPI-001" testId={TID.contractsStatCard("active")} />
          <KpiCard metricId="CON-KPI-002" testId={TID.contractsStatCard("expiring_30")} />
          <KpiCard metricId="CON-KPI-003" testId={TID.contractsStatCard("expiring_90")} />
          <KpiCard metricId="CON-KPI-004" testId={TID.contractsStatCard("expired")} />
          <KpiCard metricId="CON-KPI-005" testId={TID.contractsStatCard("at_risk")} />
          <KpiCard metricId="CON-KPI-006" testId={TID.contractsStatCard("missing_owner")} />
        </div>

        {/* Renewal timeline */}
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-semibold text-slate-800">Renewal timeline (next 12 months)</div>
              <div className="text-xs text-slate-500">Active contract count &amp; annual value by expiry month.</div>
            </div>
            {stats && (
              <div className="text-xs text-slate-600">
                Revenue at risk (90d): <span className="font-semibold text-rose-700">{fmtMoney(stats.revenue_at_risk_90d)}</span>
                <span className="mx-2 text-slate-300">·</span>
                Total active annual value: <span className="font-semibold text-slate-800">{fmtMoney(stats.total_active_annual_value)}</span>
              </div>
            )}
          </div>
          {timeline.length === 0 ? (
            <div className="text-sm text-slate-500 italic py-8 text-center">
              No contracts expiring in the next 12 months.
            </div>
          ) : (
            <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
              {timeline.map((m) => {
                const pct = Math.max(6, Math.round(((m.value || 0) / maxMonthValue) * 100));
                return (
                  <div key={m.month} data-testid={`contracts-timeline-${m.month}`}
                       className="flex flex-col items-center text-center">
                    <div className="w-full h-24 flex items-end">
                      <div className="w-full rounded-t bg-gradient-to-t from-rose-400 to-rose-200"
                           style={{ height: `${pct}%` }}
                           title={`${m.count} contracts · ${fmtMoney(m.value)}`} />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">{monthLabel(m.month)}</div>
                    <div className="text-[11px] font-semibold text-slate-700">{m.count}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* CPI schedule — next 90 days */}
        <CpiSchedulePanel items={cpiSchedule} />

        {/* Filters */}
        <div className="rounded-lg border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by contract, client or counterparty…"
              className="pl-8" data-testid={TID.contractsSearch}
            />
          </div>
          <Select value={status || "__all"} onValueChange={(v) => setStatus(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-[160px]" data-testid={TID.contractsFilterStatus}>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All statuses</SelectItem>
              {CONTRACT_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={type || "__all"} onValueChange={(v) => setType(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-[180px]" data-testid={TID.contractsFilterType}>
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All types</SelectItem>
              {CONTRACT_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant={expiringOnly ? "default" : "outline"} size="sm"
            onClick={() => setExpiringOnly((v) => !v)}
            data-testid="contracts-expiring-toggle"
          >
            <TimerReset className="w-4 h-4 mr-1.5" />
            Expiring 90d only
          </Button>
        </div>

        {/* Register */}
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <SortableTh label="Contract" k="name" sort={contractSort} setSort={setContractSort} />
                <SortableTh label="Client" k="client_name" sort={contractSort} setSort={setContractSort} />
                <SortableTh label="Type" k="contract_type" sort={contractSort} setSort={setContractSort} />
                <SortableTh label="Status" k="status" sort={contractSort} setSort={setContractSort} />
                <SortableTh label="End" k="end_date" sort={contractSort} setSort={setContractSort} />
                <SortableTh label="Annual $" k="annual_value" sort={contractSort} setSort={setContractSort} className="text-right px-3 py-2 font-medium" />
                <SortableTh label="Renewal owner" k="renewal_owner" sort={contractSort} setSort={setContractSort} />
                <th className="text-right px-3 py-2 font-medium">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-6 text-center text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-slate-500">
                  <AlertCircle className="w-6 h-6 mx-auto mb-2 opacity-50" />
                  No contracts match your filters. Click <b>New contract</b> to add one.
                </td></tr>
              ) : sortedContracts.map((c) => {
                const bucket = c.expiry_bucket?.bucket || "unknown";
                return (
                  <tr key={c.id} data-testid={TID.contractsRow(c.id)} className="border-t hover:bg-slate-50/60">
                    <td className="px-3 py-2 font-medium text-slate-800">{c.name}</td>
                    <td className="px-3 py-2 text-slate-700">{c.client_name || "—"}</td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-slate-200 text-slate-600">
                        {CONTRACT_TYPES.find((t) => t.value === c.contract_type)?.label || c.contract_type}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${BUCKET_COLOURS[bucket]}`}>
                        {c.status === "renewed" ? "Renewed" :
                          c.status === "terminated" ? "Terminated" :
                          BUCKET_LABEL[bucket]}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {fmtDate(c.end_date)}
                      {c.expiry_bucket?.days != null && (
                        <span className="ml-1 text-xs text-slate-400">
                          ({c.expiry_bucket.days >= 0 ? `${c.expiry_bucket.days}d` : `${-c.expiry_bucket.days}d ago`})
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-700">{fmtMoney(c.annual_value)}</td>
                    <td className="px-3 py-2 text-slate-700">{c.renewal_owner || <span className="text-rose-500 text-xs">Missing</span>}</td>
                    <td className="px-3 py-2 text-right space-x-2">
                      {canEdit && (
                        <Button size="sm" variant="ghost"
                                data-testid={TID.contractsDraftEmail(c.id)}
                                title="Draft renewal email with AI"
                                onClick={() => { setEmailTarget(c); setEmailOpen(true); }}>
                          <Sparkles className="w-3.5 h-3.5 mr-1 text-violet-600" /> Draft email
                        </Button>
                      )}
                      {canEdit && (
                        <Button size="sm" variant="ghost"
                                data-testid={TID.contractsRenewOpen(c.id)}
                                onClick={() => { setRenewTarget(c); setRenewOpen(true); }}>
                          <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Renew
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="sm" variant="ghost"
                                data-testid={TID.contractsDelete(c.id)}
                                onClick={async () => {
                                  if (!window.confirm(`Delete contract "${c.name}"?`)) return;
                                  try {
                                    await api.delete(`/contracts/${c.id}`);
                                    toast.success("Contract deleted");
                                    load();
                                  } catch (e) {
                                    toast.error("Delete failed", { description: formatApiErrorDetail(e) });
                                  }
                                }}>
                          <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={load} />
      <RenewDialog open={renewOpen} onOpenChange={setRenewOpen}
                   contract={renewTarget} onSaved={load} />
      <DraftEmailDialog open={emailOpen} onOpenChange={setEmailOpen}
                        contract={emailTarget} />
      <PdfImportDialog open={pdfImportOpen} onOpenChange={setPdfImportOpen}
                       onImported={load} />
    </AppLayout>
  );
}


// ============================================================
// Create contract dialog
// ============================================================
function CreateDialog({ open, onOpenChange, onSaved }) {
  const [f, setF] = useState({
    name: "", client_name: "", contract_type: "service_agreement",
    status: "active", start_date: "", end_date: "", annual_value: "",
    renewal_owner: "", counterparty: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

  const submit = async () => {
    if (!f.name.trim()) return toast.error("Contract name is required");
    setSaving(true);
    try {
      await api.post("/contracts", {
        ...f,
        annual_value: f.annual_value ? Number(f.annual_value) : null,
      });
      toast.success("Contract created");
      onOpenChange(false);
      setF({ name: "", client_name: "", contract_type: "service_agreement",
             status: "active", start_date: "", end_date: "", annual_value: "",
             renewal_owner: "", counterparty: "", notes: "" });
      onSaved?.();
    } catch (e) {
      toast.error("Create failed", { description: formatApiErrorDetail(e) });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New contract</DialogTitle>
          <DialogDescription>Register a new client contract, service agreement or warranty.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-slate-500">Contract name *</label>
            <Input value={f.name} onChange={set("name")}
                   data-testid={TID.contractsCreateName}
                   placeholder="e.g. UoN Facilities Maintenance FY26" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-500">Client name</label>
            <Input value={f.client_name} onChange={set("client_name")}
                   data-testid={TID.contractsCreateClient}
                   placeholder="University of Newcastle" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Type</label>
            <Select value={f.contract_type} onValueChange={(v) => setF((s) => ({ ...s, contract_type: v }))}>
              <SelectTrigger data-testid={TID.contractsCreateType}><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONTRACT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Status</label>
            <Select value={f.status} onValueChange={(v) => setF((s) => ({ ...s, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONTRACT_STATUSES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Start date</label>
            <Input type="date" value={f.start_date} onChange={set("start_date")} />
          </div>
          <div>
            <label className="text-xs text-slate-500">End date</label>
            <Input type="date" value={f.end_date} onChange={set("end_date")}
                   data-testid={TID.contractsCreateEndDate} />
          </div>
          <div>
            <label className="text-xs text-slate-500">Annual value ($)</label>
            <Input type="number" value={f.annual_value} onChange={set("annual_value")}
                   data-testid={TID.contractsCreateAnnualValue}
                   placeholder="120000" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Renewal owner</label>
            <Input value={f.renewal_owner} onChange={set("renewal_owner")}
                   data-testid={TID.contractsCreateRenewalOwner}
                   placeholder="commercial@gbegroup.com" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-500">Counterparty</label>
            <Input value={f.counterparty} onChange={set("counterparty")} placeholder="e.g. Anglican Diocese" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-500">Notes</label>
            <Textarea rows={2} value={f.notes} onChange={set("notes")} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving}
                  data-testid={TID.contractsCreateSubmit}
                  className="bg-gbe hover:bg-gbe/90 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create contract"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
// Renew / record renewal action dialog
// ============================================================
function RenewDialog({ open, onOpenChange, contract, onSaved }) {
  const [action, setAction] = useState("renewed");
  const [newEnd, setNewEnd] = useState("");
  const [newValue, setNewValue] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAction("renewed");
    setNewEnd("");
    setNewValue(contract?.annual_value ? String(contract.annual_value) : "");
    setNotes("");
  }, [contract]);

  if (!contract) return null;

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/contracts/${contract.id}/renewals`, {
        action,
        new_end_date: newEnd || null,
        new_annual_value: newValue ? Number(newValue) : null,
        notes: notes || null,
      });
      toast.success("Renewal recorded");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error("Renewal failed", { description: formatApiErrorDetail(e) });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record renewal action</DialogTitle>
          <DialogDescription>
            {contract.name} — {contract.client_name || "no client"} — ends {fmtDate(contract.end_date)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500">Action</label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {RENEWAL_ACTIONS.map((a) => (
                  <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(action === "renewed" || action === "extended") && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">New end date</label>
                <Input type="date" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-slate-500">New annual value ($)</label>
                <Input type="number" value={newValue} onChange={(e) => setNewValue(e.target.value)} />
              </div>
            </div>
          )}
          <div>
            <label className="text-xs text-slate-500">Notes</label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Optional context for the renewal decision." />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving}
                  data-testid={TID.contractsRenewSubmit}
                  className="bg-gbe hover:bg-gbe/90 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save renewal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ============================================================
// AI: Draft renewal email dialog
// ============================================================
function DraftEmailDialog({ open, onOpenChange, contract }) {
  const [tone, setTone] = useState("professional");
  const [priceHint, setPriceHint] = useState(true);
  const [signature, setSignature] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) {
      setResult(null); setLoading(false); setCopied(false); setSendTo(""); setSending(false);
    } else {
      // Prefill recipient with the renewal owner if it looks like an email
      const owner = contract?.renewal_owner || "";
      if (/@/.test(owner)) setSendTo(owner);
    }
  }, [open, contract]);

  if (!contract) return null;

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/contracts/${contract.id}/draft-renewal-email`, {
        tone,
        include_price_hint: priceHint,
        signature: signature || null,
      });
      setResult(data);
    } catch (e) {
      toast.error("AI draft failed", { description: formatApiErrorDetail(e) });
    } finally { setLoading(false); }
  };

  const copyAll = async () => {
    if (!result) return;
    const text = `Subject: ${result.subject}\n\n${result.body}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true); toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copy failed");
    }
  };

  const mailto = result
    ? `mailto:?subject=${encodeURIComponent(result.subject)}&body=${encodeURIComponent(result.body)}`
    : null;

  const sendViaResend = async () => {
    if (!result) return;
    if (!/@/.test(sendTo || "")) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setSending(true);
    try {
      await api.post(`/contracts/${contract.id}/send-renewal-email`, {
        to_email: sendTo,
        subject: result.subject,
        body: result.body,
        contract_id: contract.id,
        kind: "contract_renewal",
      });
      toast.success(`Email sent to ${sendTo}`);
      onOpenChange(false);
    } catch (e) {
      toast.error("Send failed", { description: formatApiErrorDetail(e) });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-600" />
            Draft renewal email
          </DialogTitle>
          <DialogDescription>
            {contract.name} — {contract.client_name || "no client"} — ends {fmtDate(contract.end_date)}
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-500">Tone</label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="friendly">Friendly</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={priceHint}
                     onChange={(e) => setPriceHint(e.target.checked)} />
              Suggest a CPI-linked price uplift
            </label>
            <div>
              <label className="text-xs text-slate-500">Signature (optional)</label>
              <Textarea rows={2} value={signature} onChange={(e) => setSignature(e.target.value)}
                        placeholder="Darren Andrews&#10;Director, GBE Group" />
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Subject</div>
              <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium">
                {result.subject}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Body</div>
              <Textarea rows={12} value={result.body}
                        onChange={(e) => setResult({ ...result, body: e.target.value })}
                        className="font-mono text-xs" />
            </div>
            {result.notes_for_sender && (
              <div className="text-xs text-slate-500 italic">
                <b>Note:</b> {result.notes_for_sender}
              </div>
            )}
            <div className="text-[11px] text-slate-400">
              Model: {result.model} · Confidence {result.confidence}%
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
              <Button onClick={generate} disabled={loading}
                      className="bg-violet-600 hover:bg-violet-700 text-white">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Drafting…</> :
                          <><Sparkles className="w-4 h-4 mr-2" />Generate draft</>}
              </Button>
            </>
          ) : (
            <div className="flex flex-col gap-2 w-full">
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  value={sendTo}
                  onChange={(e) => setSendTo(e.target.value)}
                  placeholder="recipient@example.com"
                  data-testid="contracts-draft-email-recipient"
                  className="flex-1"
                />
                <Button variant="outline" onClick={copyAll}
                        data-testid={TID.contractsDraftEmailCopy}>
                  {copied ? <><CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" />Copied</> :
                            <><Copy className="w-4 h-4 mr-2" />Copy</>}
                </Button>
                <Button variant="ghost" onClick={() => setResult(null)}>Regenerate</Button>
                <Button onClick={sendViaResend} disabled={sending}
                        data-testid="contracts-draft-email-send"
                        className="bg-gbe hover:bg-gbe/90 text-white">
                  {sending ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Sending…</> :
                             <><Mail className="w-4 h-4 mr-2" />Send</>}
                </Button>
              </div>
              <div className="text-[11px] text-slate-400">
                Send delivers via Resend and logs to Audit Trail + Email Log.
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
// AI: Import contract from PDF dialog
// ============================================================
function PdfImportDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open) { setFile(null); setResult(null); setLoading(false); setForm(null); }
  }, [open]);

  const upload = async () => {
    if (!file) return toast.error("Select a PDF file first");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/contracts/extract-from-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setResult(data);
      // Prefill an editable form from the extracted values
      setForm({
        name: data.extracted?.name || "",
        client_name: data.extracted?.client_name || "",
        contract_type: data.extracted?.contract_type || "service_agreement",
        status: "active",
        start_date: data.extracted?.start_date || "",
        end_date: data.extracted?.end_date || "",
        annual_value: data.extracted?.annual_value ?? "",
        total_value: data.extracted?.total_value ?? "",
        counterparty: data.extracted?.counterparty || "",
        auto_renew: data.extracted?.auto_renew ?? false,
        notice_period_days: data.extracted?.notice_period_days ?? "",
      });
    } catch (e) {
      toast.error("PDF extraction failed", { description: formatApiErrorDetail(e) });
    } finally { setLoading(false); }
  };

  const use = async () => {
    if (!form) return;
    if (!form.name?.trim()) return toast.error("Contract name is required");
    setSaving(true);
    try {
      await api.post("/contracts", {
        ...form,
        annual_value: form.annual_value ? Number(form.annual_value) : null,
        total_value: form.total_value ? Number(form.total_value) : null,
        notice_period_days: form.notice_period_days ? Number(form.notice_period_days) : null,
      });
      toast.success("Contract created from PDF");
      onOpenChange(false);
      onImported?.();
    } catch (e) {
      toast.error("Save failed", { description: formatApiErrorDetail(e) });
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-600" />
            Import contract from PDF
          </DialogTitle>
          <DialogDescription>
            Upload a signed contract PDF; Claude will extract name, client, dates &amp; value.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-3">
            <label className="block">
              <div className="text-xs text-slate-500 mb-1">PDF file (max 20 MB)</div>
              <input type="file" accept="application/pdf"
                     onChange={(e) => setFile(e.target.files?.[0] || null)}
                     data-testid={TID.contractsPdfImportFile}
                     className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-3
                                file:rounded file:border-0 file:text-sm file:font-medium
                                file:bg-violet-50 file:text-violet-700
                                hover:file:bg-violet-100" />
            </label>
            {file && (
              <div className="text-xs text-slate-500">
                Selected: <span className="font-mono">{file.name}</span> · {(file.size / 1024).toFixed(0)} KB
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {result.extracted?.warnings?.length ? (
              <div className="rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2">
                <b>Warnings from the extractor:</b>
                <ul className="list-disc list-inside">
                  {result.extracted.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            ) : null}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-slate-500">Contract name *</label>
                <Input value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label className="text-xs text-slate-500">Client</label>
                <Input value={form.client_name} onChange={set("client_name")} />
              </div>
              <div>
                <label className="text-xs text-slate-500">Counterparty</label>
                <Input value={form.counterparty} onChange={set("counterparty")} />
              </div>
              <div>
                <label className="text-xs text-slate-500">Start date</label>
                <Input type="date" value={form.start_date} onChange={set("start_date")} />
              </div>
              <div>
                <label className="text-xs text-slate-500">End date</label>
                <Input type="date" value={form.end_date} onChange={set("end_date")} />
              </div>
              <div>
                <label className="text-xs text-slate-500">Annual value ($)</label>
                <Input type="number" value={form.annual_value} onChange={set("annual_value")} />
              </div>
              <div>
                <label className="text-xs text-slate-500">Total value ($)</label>
                <Input type="number" value={form.total_value} onChange={set("total_value")} />
              </div>
              <div>
                <label className="text-xs text-slate-500">Notice period (days)</label>
                <Input type="number" value={form.notice_period_days} onChange={set("notice_period_days")} />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600 mt-6">
                <input type="checkbox" checked={!!form.auto_renew}
                       onChange={(e) => setForm((s) => ({ ...s, auto_renew: e.target.checked }))} />
                Auto-renew
              </label>
            </div>
            <div className="text-[11px] text-slate-400">
              Model: {result.model} · Extractor confidence {result.extracted?.confidence ?? "—"}%
              {result.extraction_method === "vision" && (
                <span className="ml-2 inline-flex items-center gap-1 text-violet-700">
                  <Sparkles className="w-3 h-3" /> Vision fallback used (scanned PDF)
                </span>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>Cancel</Button>
              <Button onClick={upload} disabled={loading || !file}
                      data-testid={TID.contractsPdfImportSubmit}
                      className="bg-violet-600 hover:bg-violet-700 text-white">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Extracting…</> :
                          <><Upload className="w-4 h-4 mr-2" />Extract with AI</>}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => { setResult(null); setFile(null); setForm(null); }}>
                Upload another
              </Button>
              <Button onClick={use} disabled={saving}
                      data-testid={TID.contractsPdfImportUse}
                      className="bg-gbe hover:bg-gbe/90 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save contract"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// CpiSchedulePanel
// Shows contracts with an upcoming CPI review (annual anniversary of
// start_date, or explicit next_cpi_date override) in the next 90 days.
// ============================================================
function CpiSchedulePanel({ items }) {
  const dueSoon = items.filter((r) => (r.days_until_cpi ?? 999) <= 30).length;
  const total = items.length;

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4"
      data-testid="contracts-cpi-schedule-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-semibold text-slate-800 flex items-center gap-2">
            <TimerReset className="w-4 h-4 text-amber-600" />
            CPI schedule (next 90 days)
          </div>
          <div className="text-xs text-slate-500">
            Contracts due for CPI review — derived from start-date anniversary or an
            explicit <code className="text-[10px] px-1 py-0.5 rounded bg-slate-100">next_cpi_date</code> override.
          </div>
        </div>
        <div className="text-xs text-slate-600" data-testid="contracts-cpi-count-badge">
          {total} due · <span className="font-semibold text-amber-700">{dueSoon}</span> within 30d
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-6 text-center"
             data-testid="contracts-cpi-empty">
          No CPI reviews due in the next 90 days.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b">
                <th className="py-2 pr-4">Contract</th>
                <th className="py-2 pr-4">Client</th>
                <th className="py-2 pr-4">Owner</th>
                <th className="py-2 pr-4 text-right">Annual value</th>
                <th className="py-2 pr-4">CPI due</th>
                <th className="py-2">Days</th>
              </tr>
            </thead>
            <tbody>
              {items.slice(0, 50).map((c) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-slate-50"
                    data-testid={`contracts-cpi-row-${c.id}`}>
                  <td className="py-2 pr-4">
                    <div className="font-medium text-slate-800">{c.name || c.contract_number || "—"}</div>
                    <div className="text-[11px] text-slate-500">{c.contract_number || ""}</div>
                  </td>
                  <td className="py-2 pr-4 text-slate-700">{c.client_name || "—"}</td>
                  <td className="py-2 pr-4 text-slate-700">{c.renewal_owner || "—"}</td>
                  <td className="py-2 pr-4 text-right text-slate-800">{fmtMoney(c.annual_value)}</td>
                  <td className="py-2 pr-4 text-slate-700">
                    {fmtDate(c.next_cpi_date)}
                    {c.source === "start_date_anniversary" && (
                      <span className="ml-1 text-[10px] text-slate-400" title="Derived from start-date anniversary">
                        (derived)
                      </span>
                    )}
                  </td>
                  <td className="py-2">
                    <Badge
                      variant="outline"
                      className={
                        (c.days_until_cpi ?? 999) <= 30
                          ? "bg-amber-100 text-amber-800 border-amber-200"
                          : "bg-slate-100 text-slate-700 border-slate-200"
                      }
                      data-testid={`contracts-cpi-days-${c.id}`}
                    >
                      {c.days_until_cpi != null ? `${c.days_until_cpi}d` : "—"}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length > 50 && (
            <div className="text-[11px] text-slate-500 italic mt-2">
              Showing 50 of {items.length}. Refine the CPI window on the backend to see the rest.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

